// @ts-check
import { Edit2, MoreHorizontal, Plus, Save, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import ImageUploadPreview from '../components/ImageUploadPreview';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import api from '../utils/apiClient';

const TaxonomyPage = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('product-types');
  const [data, setData] = useState({
    productTypes: [],
    categories: [],
    events: [],
    moods: [],
    places: [],
    features: [],
  });
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [types, categories, events, moods, places, features] = await Promise.all([
        api.get('/api/admin/taxonomy/product-types'),
        api.get('/api/admin/taxonomy/product-type-categories'),
        api.get('/api/admin/taxonomy/events'),
        api.get('/api/admin/taxonomy/moods'),
        api.get('/api/admin/taxonomy/places'),
        api.get('/api/admin/taxonomy/features'),
      ]);
      setData({ productTypes: types, categories, events, moods, places, features });
    } catch (err) {
      toast.error('Failed to load taxonomy data');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tabs = [
    { key: 'product-types', label: 'Product Types', count: data.productTypes.length },
    { key: 'categories', label: 'Categories', count: data.categories.length },
    { key: 'occasions', label: 'Occasions', count: data.events.length },
    { key: 'moods', label: 'Moods', count: data.moods.length },
    { key: 'places', label: 'Places', count: data.places.length },
    { key: 'features', label: 'Features', count: data.features.length },
  ];

  const categoryOptions = data.categories.map((c) => ({ value: c.id, label: c.name }));
  const categoryNameById = data.categories.reduce((acc, c) => {
    acc[c.id] = c.name;
    return acc;
  }, {});

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-serif mb-2">Occasions, Moods, Places & Features</h1>
        <p className="text-muted-foreground">
          Manage product classifications used in the header mega menu. Only items linked to published products will
          appear on the customer site.
        </p>
      </div>

      <div className="border-b border-border mb-6">
        <div className="flex gap-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === tab.key
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {activeTab === 'product-types' && (
            <TaxonomyCRUD
              items={data.productTypes}
              onUpdate={loadData}
              toast={toast}
              endpoint="/api/admin/taxonomy/product-types"
              fields={[
                { key: 'name', label: 'Name', required: true },
                { key: 'slug', label: 'Slug', required: true },
                {
                  key: 'category_id',
                  label: 'Category',
                  type: 'select',
                  options: categoryOptions,
                  emptyLabel: 'No category',
                },
                {
                  key: 'default_hsn_code',
                  label: 'Default HSN Code',
                  hint: 'Pre-fills the HSN code on new products of this type.',
                },
                {
                  key: 'default_tax_rate',
                  label: 'Default Tax Rate (%)',
                  type: 'number',
                  hint: 'Pre-fills the tax rate on new products of this type.',
                },
                {
                  key: 'default_weight_kg',
                  label: 'Default Parcel Weight (kg)',
                  type: 'number',
                  hint: 'Pre-fills packed weight on new products of this type.',
                },
                {
                  key: 'default_length_cm',
                  label: 'Default Parcel Length (cm)',
                  type: 'number',
                },
                {
                  key: 'default_breadth_cm',
                  label: 'Default Parcel Breadth (cm)',
                  type: 'number',
                },
                {
                  key: 'default_height_cm',
                  label: 'Default Parcel Height (cm)',
                  type: 'number',
                },
                { key: 'is_active', label: 'Active', type: 'checkbox' },
              ]}
              nameField="name"
              showSlug
              showActive
              showCategory
              categoryNameById={categoryNameById}
              canDelete
            />
          )}
          {activeTab === 'categories' && (
            <TaxonomyCRUD
              items={data.categories}
              onUpdate={loadData}
              toast={toast}
              endpoint="/api/admin/taxonomy/product-type-categories"
              fields={[
                { key: 'name', label: 'Name', required: true },
                { key: 'slug', label: 'Slug', required: true },
                { key: 'is_active', label: 'Active', type: 'checkbox' },
                {
                  key: 'hero_image',
                  label: 'Hero Image (Desktop — landscape)',
                  type: 'image',
                  uploadType: 'category-image',
                  full: true,
                },
                {
                  key: 'mobile_hero_image',
                  label: 'Mobile Hero Image (Portrait — shown on phones)',
                  type: 'image',
                  uploadType: 'category-image',
                  full: true,
                },
              ]}
              nameField="name"
              showSlug
              showActive
              canDelete
            />
          )}
          {activeTab === 'occasions' && (
            <TaxonomyCRUD
              items={data.events}
              onUpdate={loadData}
              toast={toast}
              endpoint="/api/admin/taxonomy/events"
              fields={[
                { key: 'name', label: 'Name', required: true },
                { key: 'slug', label: 'Slug', required: true },
                { key: 'description', label: 'Description' },
              ]}
              nameField="name"
              showSlug
              canDelete
            />
          )}
          {activeTab === 'moods' && (
            <TaxonomyCRUD
              items={data.moods}
              onUpdate={loadData}
              toast={toast}
              endpoint="/api/admin/taxonomy/moods"
              fields={[
                { key: 'name', label: 'Name', required: true },
                { key: 'slug', label: 'Slug', required: true },
                { key: 'description', label: 'Description' },
              ]}
              nameField="name"
              showSlug
              canDelete
            />
          )}
          {activeTab === 'places' && (
            <TaxonomyCRUD
              items={data.places}
              onUpdate={loadData}
              toast={toast}
              endpoint="/api/admin/taxonomy/places"
              fields={[
                { key: 'name', label: 'Name', required: true },
                { key: 'slug', label: 'Slug', required: true },
                { key: 'description', label: 'Description' },
              ]}
              nameField="name"
              showSlug
              canDelete
            />
          )}
          {activeTab === 'features' && (
            <TaxonomyCRUD
              items={data.features}
              onUpdate={loadData}
              toast={toast}
              endpoint="/api/admin/taxonomy/features"
              fields={[
                { key: 'name', label: 'Name', required: true },
                { key: 'slug', label: 'Slug', required: true },
                { key: 'description', label: 'Description' },
              ]}
              nameField="name"
              showSlug
              canDelete
            />
          )}
        </>
      )}
    </div>
  );
};

/* ========== Reusable CRUD Component ========== */
const TaxonomyCRUD = ({
  items,
  onUpdate,
  toast,
  endpoint,
  fields,
  nameField,
  showSlug,
  showActive = false,
  showCategory = false,
  categoryNameById = {},
  canDelete,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  /** @type {[Record<string, any>, Function]} */
  const [formData, setFormData] = useState({});
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const resetForm = () => {
    setFormData({});
    setEditingItem(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    const fd = {};
    fields.forEach((f) => {
      fd[f.key] = item[f.key] ?? (f.type === 'checkbox' ? true : '');
    });
    setFormData(fd);
    setEditingItem(item);
    setShowForm(true);
  };

  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      slug: editingItem ? prev.slug : generateSlug(value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Select fields use '' to mean "no selection" in the form; the API expects null there.
      const payload = { ...formData };
      fields.forEach((f) => {
        if ((f.type === 'select' || f.type === 'number') && payload[f.key] === '') {
          payload[f.key] = null;
        }
      });
      if (editingItem) {
        await api.patch(`${endpoint}/${editingItem.id}`, payload);
        toast.success('Updated successfully');
      } else {
        await api.post(endpoint, payload);
        toast.success('Created successfully');
      }
      resetForm();
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item[nameField]}"? This cannot be undone.`)) return;
    try {
      await api.del(`${endpoint}/${item.id}`);
      toast.success('Deleted');
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-medium">{items.length} items</h2>
        {!showForm && (
          <button
            type="button"
            onClick={() => {
              setFormData(
                fields.reduce((acc, f) => {
                  acc[f.key] = f.type === 'checkbox' ? true : '';
                  return acc;
                }, {}),
              );
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={18} />
            Add New
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-muted/50 border border-border rounded-lg p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <h3 className="text-lg font-medium mb-4">{editingItem ? `Edit: ${editingItem[nameField]}` : 'Add New'}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {fields.map((field) =>
                field.type === 'image' ? (
                  <div key={field.key} className={field.full ? 'col-span-2' : ''}>
                    <ImageUploadPreview
                      value={formData[field.key]}
                      onChange={(url) => setFormData({ ...formData, [field.key]: url })}
                      label={field.label}
                      uploadType={field.uploadType || 'product-image'}
                    />
                  </div>
                ) : field.type === 'checkbox' ? (
                  <div key={field.key} className="flex items-center pt-6">
                    <input
                      type="checkbox"
                      id={`tax_${field.key}`}
                      checked={formData[field.key] ?? true}
                      onChange={(e) => setFormData({ ...formData, [field.key]: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor={`tax_${field.key}`} className="text-sm font-medium">
                      {field.label}
                    </label>
                  </div>
                ) : field.type === 'select' ? (
                  <div key={field.key}>
                    <label className="block text-sm font-medium mb-1">
                      {field.label} {field.required && '*'}
                    </label>
                    <select
                      value={formData[field.key] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                      required={field.required}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground bg-card"
                    >
                      <option value="">{field.emptyLabel || '— None —'}</option>
                      {(field.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div key={field.key}>
                    <label className="block text-sm font-medium mb-1">
                      {field.label} {field.required && '*'}
                    </label>
                    <input
                      type={field.type === 'number' ? 'number' : 'text'}
                      step={field.type === 'number' ? '0.01' : undefined}
                      min={field.type === 'number' ? '0' : undefined}
                      value={formData[field.key] ?? ''}
                      onChange={(e) =>
                        field.key === 'name'
                          ? handleNameChange(e.target.value)
                          : setFormData({ ...formData, [field.key]: e.target.value })
                      }
                      required={field.required}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                      placeholder={field.key === 'slug' ? 'auto-generated' : ''}
                    />
                    {field.hint && <p className="text-xs text-muted-foreground mt-1">{field.hint}</p>}
                  </div>
                ),
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save size={18} />
                {editingItem ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Name</th>
              {showSlug && (
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Slug</th>
              )}
              {showCategory && (
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Category</th>
              )}
              {showActive && (
                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              )}
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={2 + (showSlug ? 1 : 0) + (showCategory ? 1 : 0) + (showActive ? 1 : 0)}
                  className="px-6 py-12 text-center text-muted-foreground"
                >
                  No items yet. Click "Add New" to create one.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4 font-medium text-foreground">{item[nameField]}</td>
                  {showSlug && <td className="px-6 py-4 text-sm text-muted-foreground">{item.slug}</td>}
                  {showCategory && (
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {categoryNameById[item.category_id] || '—'}
                    </td>
                  )}
                  {showActive && (
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-block px-2 py-1 rounded-md text-xs ${
                          item.is_active
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {item.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  )}
                  <td className="px-6 py-4 text-center">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === item.id ? null : item.id)}
                        className="p-1 hover:bg-accent rounded-lg"
                      >
                        <MoreHorizontal size={18} />
                      </button>
                      {openActionMenu === item.id && (
                        <div className="absolute right-0 mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => {
                              handleEdit(item);
                              setOpenActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                          >
                            <Edit2 size={14} /> Edit
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => {
                                handleDelete(item);
                                setOpenActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaxonomyPage;
